import { z } from "zod";

export const contentStatusSchema = z.enum([
  "draft",
  "in_review",
  "published",
  "archived",
]);

export type ContentStatus = z.infer<typeof contentStatusSchema>;

export const frameworkTypeSchema = z.enum([
  "framework",
  "methodology",
  "standard",
  "protocol",
]);

export type FrameworkType = z.infer<typeof frameworkTypeSchema>;

// ─── CIH V3 — Coach Business Builder vertical ───────────────────────────────

export const guideTypeSchema = z.enum(["stage_guide", "tool", "comparison"]);
export type GuideType = z.infer<typeof guideTypeSchema>;

export const practiceStageSchema = z.enum([
  "starting",
  "growing",
  "scaling",
  "all",
]);
export type PracticeStage = z.infer<typeof practiceStageSchema>;

/**
 * Annual revenue stage band, aligned 1:1 with the Nova Diagnose stages
 * (`PRCPR_STAGES` in `~/lib/shared/nova/diagnose/prcpr-v3-questionnaire`): Starter <$25K,
 * Earner $25–60K, Builder $60–150K, Scaler $150K+ trailing-twelve-month
 * revenue — plus "all" for content that carries no stage floor.
 *
 * Deliberately separate from `practiceStageSchema`, which is the older
 * *monthly* three-band cut (starting/growing/scaling) still driving the live
 * library rails. The band columns are populated from the Knowledge Layer
 * Mapping sheet, and the rails cut over to them once every row carries one.
 * The closing CBB box already reads `ctaStageBand`.
 *
 * Backs the `practice_stage_band` Postgres enum directly — see `drizzle/schema`.
 */
export const practiceStageBandSchema = z.enum([
  "starter",
  "earner",
  "builder",
  "scaler",
  "all",
]);
export type PracticeStageBand = z.infer<typeof practiceStageBandSchema>;

/**
 * PrcPR link — which link of the coach acquisition chain a page serves.
 *
 * Promise → Reach → Conversion → Proof → Retention are the five chain links;
 * "chain" marks whole-chain content (the roadmap, the money pages) and
 * "practice" marks operations content sitting outside the chain entirely
 * (hiring, contracts, tech stack). Supersedes the retired v3.1
 * Demand/Leverage/Ownership/Delivery trigger vocabulary.
 *
 * Backs the `prcpr_link` Postgres enum directly — see `drizzle/schema`.
 */
export const prcprLinkSchema = z.enum([
  "promise",
  "reach",
  "conversion",
  "proof",
  "retention",
  "chain",
  "practice",
]);
export type PrcprLink = z.infer<typeof prcprLinkSchema>;

/**
 * Library shelf — which index rail a guide or playbook renders on at
 * `/coaching-business`. Seven stable values: `spine_hero` and
 * `spine_companion` are the hand-picked trio atop the index, the four stage
 * shelves (`starting_out` through `scaling`) order the rows a coach's
 * practice graduates through, and `canon` is the playbooks rail.
 *
 * A shelf is not a revenue band under another name — `starting_out` and
 * `canon` each mix multiple `practiceStageBand` floors, so nothing here
 * should be read as, or labeled with, a stage cutoff.
 *
 * Backs the `library_shelf` Postgres enum directly — see `drizzle/schema`.
 */
export const libraryShelfSchema = z.enum([
  "spine_hero",
  "spine_companion",
  "starting_out",
  "getting_consistent",
  "building",
  "scaling",
  "canon",
]);
export type LibraryShelf = z.infer<typeof libraryShelfSchema>;

/**
 * Series family — the reading sequence a page belongs to, independent of the
 * shelf it renders on. Three of the four families cross shelves, so series
 * navigation is a detail-page previous/next link, never a rail of its own.
 *
 * Backs the `series_family` Postgres enum directly — see `drizzle/schema`.
 */
export const seriesFamilySchema = z.enum([
  "proof",
  "stage_ladder",
  "sales_motion",
  "channel",
]);
export type SeriesFamily = z.infer<typeof seriesFamilySchema>;

export const ipSensitivitySchema = z.enum(["high", "medium", "low"]);
export type IpSensitivity = z.infer<typeof ipSensitivitySchema>;

export const partnerTierSchema = z.enum(["tier1", "tier2", "tier3"]);
export type PartnerTier = z.infer<typeof partnerTierSchema>;

export const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  draft: "Draft",
  in_review: "In review",
  published: "Published",
  archived: "Archived",
};

export const CONTENT_STATUS_COLORS: Record<ContentStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  in_review: "bg-amber-100 text-amber-700",
  published: "bg-green-100 text-green-700",
  archived: "bg-gray-100 text-gray-500",
};

/** Filter options for content status lists (includes "All" with empty value). */
export const CONTENT_STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "in_review", label: "In Review" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
] as const;

/** Dropdown options for framework type selects. */
export const FRAMEWORK_TYPE_OPTIONS: { value: FrameworkType; label: string }[] =
  [
    { value: "framework", label: "Framework" },
    { value: "methodology", label: "Methodology" },
    { value: "standard", label: "Standard" },
    { value: "protocol", label: "Protocol" },
  ];

// ─── CIH V3 — Coach Business Builder vertical ───────────────────────────────

export const GUIDE_TYPE_LABELS: Record<GuideType, string> = {
  stage_guide: "Stage Guide",
  tool: "Tool",
  comparison: "Comparison",
};

export const GUIDE_TYPE_OPTIONS: { value: GuideType; label: string }[] = [
  { value: "stage_guide", label: "Stage Guide" },
  { value: "tool", label: "Tool" },
  { value: "comparison", label: "Comparison" },
];

export const PRACTICE_STAGE_LABELS: Record<PracticeStage, string> = {
  starting: "Starting ($0–$3K/mo)",
  growing: "Growing ($3K–$10K/mo)",
  scaling: "Scaling ($10K+/mo)",
  all: "All stages",
};

export const PRACTICE_STAGE_OPTIONS: { value: PracticeStage; label: string }[] =
  [
    { value: "starting", label: "Starting ($0–$3K/mo)" },
    { value: "growing", label: "Growing ($3K–$10K/mo)" },
    { value: "scaling", label: "Scaling ($10K+/mo)" },
    { value: "all", label: "All stages" },
  ];

export const PRACTICE_STAGE_BAND_OPTIONS: {
  value: PracticeStageBand;
  label: string;
}[] = [
  { value: "starter", label: "Starter (<$25K/yr)" },
  { value: "earner", label: "Earner ($25K–$60K/yr)" },
  { value: "builder", label: "Builder ($60K–$150K/yr)" },
  { value: "scaler", label: "Scaler ($150K+/yr)" },
  { value: "all", label: "All bands" },
];

export const PRCPR_LINK_OPTIONS: { value: PrcprLink; label: string }[] = [
  { value: "promise", label: "Promise" },
  { value: "reach", label: "Reach" },
  { value: "conversion", label: "Conversion" },
  { value: "proof", label: "Proof" },
  { value: "retention", label: "Retention" },
  { value: "chain", label: "Whole chain" },
  { value: "practice", label: "Practice ops" },
];

export const LIBRARY_SHELF_LABELS: Record<LibraryShelf, string> = {
  spine_hero: "Spine (hero)",
  spine_companion: "Spine (companion)",
  starting_out: "Starting Out",
  getting_consistent: "Getting Consistent",
  building: "Building",
  scaling: "Scaling",
  canon: "Canon",
};

export const LIBRARY_SHELF_OPTIONS: { value: LibraryShelf; label: string }[] = [
  { value: "spine_hero", label: "Spine (hero)" },
  { value: "spine_companion", label: "Spine (companion)" },
  { value: "starting_out", label: "Starting Out" },
  { value: "getting_consistent", label: "Getting Consistent" },
  { value: "building", label: "Building" },
  { value: "scaling", label: "Scaling" },
  { value: "canon", label: "Canon" },
];

export const SERIES_FAMILY_LABELS: Record<SeriesFamily, string> = {
  proof: "Proof series",
  stage_ladder: "Stage ladder",
  sales_motion: "Sales motion",
  channel: "Channel series",
};

export const SERIES_FAMILY_OPTIONS: { value: SeriesFamily; label: string }[] = [
  { value: "proof", label: "Proof series" },
  { value: "stage_ladder", label: "Stage ladder" },
  { value: "sales_motion", label: "Sales motion" },
  { value: "channel", label: "Channel series" },
];

export const IP_SENSITIVITY_OPTIONS: { value: IpSensitivity; label: string }[] =
  [
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ];

export const PARTNER_TIER_OPTIONS: { value: PartnerTier; label: string }[] = [
  { value: "tier1", label: "Tier 1" },
  { value: "tier2", label: "Tier 2" },
  { value: "tier3", label: "Tier 3" },
];
